import React, { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import XIcon from './icons/XIcon';
import RefreshCwIcon from './icons/RefreshCwIcon';
import DownloadIcon from './icons/DownloadIcon';
import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';


// --- INLINE ICON DEFINITIONS ---
const PanToolIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M8 11V7a4 4 0 1 1 8 0v4" /><path d="M12 21a9 9 0 0 1-9-9 4 4 0 0 1 4-4h10a4 4 0 0 1 4 4 9 9 0 0 1-9 9z" />
  </svg>
);
const TransformIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M3 3v4h4" /><path d="M21 3v4h-4" /><path d="M21 21v-4h-4" /><path d="M3 21v-4h4" /><path d="M3 7h18M7 3v18" />
  </svg>
);


interface SmileStudioProps {
  faceImageUrl: string;
  smileModelUrl: string;
  onClose: () => void;
}

const initialTransform = {
  x: 0,
  y: 0,
  scale: 1,
  width: 100, // as percentage
  height: 100, // as percentage
  rotate: 0,
};

const initialFilters = {
  brightness: 100,
  contrast: 100,
  saturate: 100,
  hue: 0,
  exposure: 0,
  temperature: 6500, // In Kelvin
};

const SmileStudio: React.FC<SmileStudioProps> = ({ faceImageUrl, smileModelUrl, onClose }) => {
  const [transform, setTransform] = useState(initialTransform);
  const [filters, setFilters] = useState(initialFilters);
  const [faceOpacity, setFaceOpacity] = useState(1);
  const [isDraggingSmile, setIsDraggingSmile] = useState(false);
  const [viewState, setViewState] = useState({ x: 0, y: 0, scale: 1 });
  const [activeTool, setActiveTool] = useState<'transform' | 'pan'>('transform');
  const [isPanning, setIsPanning] = useState(false);
  const [startPanPoint, setStartPanPoint] = useState({ x: 0, y: 0 });
  const [renderedFaceDims, setRenderedFaceDims] = useState<{ width: number; height: number; } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const faceImageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const faceImg = faceImageRef.current;
    if (!faceImg) return;
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect;
        setRenderedFaceDims({ width, height });
      }
    });
    observer.observe(faceImg);
    return () => observer.disconnect();
  }, []);

  const handleReset = () => {
    setTransform(initialTransform);
    setFilters(initialFilters);
    setFaceOpacity(1);
    resetView();
  };

  const resetView = useCallback(() => {
    const container = containerRef.current;
    const faceImg = faceImageRef.current;
    if (container && faceImg && faceImg.naturalWidth > 0) {
      const { width: cW, height: cH } = container.getBoundingClientRect();
      const { naturalWidth: iW, naturalHeight: iH } = faceImg;
      const scale = Math.min(cW / iW, cH / iH) * 0.9;
      setViewState({
        scale,
        x: (cW - iW * scale) / 2,
        y: (cH - iH * scale) / 2,
      });
    }
  }, []);

  const handleZoom = (factor: number) => {
    const container = containerRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    const newScale = Math.max(0.1, Math.min(viewState.scale * factor, 20));
    const mouseX = width / 2;
    const mouseY = height / 2;
    const worldX = (mouseX - viewState.x) / viewState.scale;
    const worldY = (mouseY - viewState.y) / viewState.scale;
    const newX = mouseX - worldX * newScale;
    const newY = mouseY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  };

  const handleWheel = (e: React.WheelEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const scaleAmount = 1.1;
    const delta = e.deltaY > 0 ? 1 / scaleAmount : scaleAmount;
    const newScale = Math.max(0.1, Math.min(viewState.scale * delta, 20));
    const worldX = (mouseX - viewState.x) / viewState.scale;
    const worldY = (mouseY - viewState.y) / viewState.scale;
    const newX = mouseX - worldX * newScale;
    const newY = mouseY - worldY * newScale;
    setViewState({ scale: newScale, x: newX, y: newY });
  };

  const handlePanStart = (e: React.MouseEvent) => {
    if (activeTool !== 'pan') return;
    e.preventDefault();
    setIsPanning(true);
    setStartPanPoint({ x: e.clientX - viewState.x, y: e.clientY - viewState.y });
  };

  const handlePanMove = (e: React.MouseEvent) => {
    if (!isPanning || activeTool !== 'pan') return;
    e.preventDefault();
    const newX = e.clientX - startPanPoint.x;
    const newY = e.clientY - startPanPoint.y;
    setViewState(v => ({ ...v, x: newX, y: newY }));
  };

  const handlePanEnd = () => setIsPanning(false);

  const handleExport = async () => {
    if (!renderedFaceDims || renderedFaceDims.width === 0 || renderedFaceDims.height === 0) {
      alert("Cannot export: image dimensions not ready. Please wait a moment and try again.");
      return;
    }

    const faceImg = new Image();
    faceImg.crossOrigin = 'anonymous';
    const smileImg = new Image();
    smileImg.crossOrigin = 'anonymous';

    try {
      await Promise.all([
        new Promise<void>((resolve, reject) => { faceImg.onload = () => resolve(); faceImg.onerror = reject; faceImg.src = faceImageUrl; }),
        new Promise<void>((resolve, reject) => { smileImg.onload = () => resolve(); smileImg.onerror = reject; smileImg.src = smileModelUrl; })
      ]);

      const canvas = document.createElement('canvas');
      canvas.width = faceImg.naturalWidth;
      canvas.height = faceImg.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Draw smile layer first (underneath face) with all transforms and filters.
      ctx.save();

      ctx.filter = `brightness(${filters.brightness + filters.exposure}%) contrast(${filters.contrast}%) saturate(${filters.saturate}%) hue-rotate(${filters.hue}deg)`;

      // --- FIX: Calculate separate X and Y scaling ratios for accuracy ---
      const scaleToNaturalX = faceImg.naturalWidth / renderedFaceDims.width;
      const scaleToNaturalY = faceImg.naturalHeight / renderedFaceDims.height;

      const finalSmileCenterX = (faceImg.naturalWidth / 2) + (transform.x * scaleToNaturalX);
      const finalSmileCenterY = (faceImg.naturalHeight / 2) + (transform.y * scaleToNaturalY);

      ctx.translate(finalSmileCenterX, finalSmileCenterY);
      ctx.rotate(transform.rotate * Math.PI / 180);

      // Adjust screen-space scale to the full resolution of the canvas
      const finalScaleX = transform.scale * (transform.width / 100) * scaleToNaturalX;
      const finalScaleY = transform.scale * (transform.height / 100) * scaleToNaturalY;

      ctx.scale(finalScaleX, finalScaleY);

      ctx.drawImage(smileImg, -smileImg.naturalWidth / 2, -smileImg.naturalHeight / 2, smileImg.naturalWidth, smileImg.naturalHeight);
      ctx.restore();

      // Draw face layer on top with its opacity
      ctx.save();
      ctx.globalAlpha = faceOpacity;
      ctx.drawImage(faceImg, 0, 0);
      ctx.restore();

      // Download
      const link = document.createElement('a');
      link.download = 'FuturaSmile_Studio_Design.jpg';
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    } catch (error) {
      console.error("Error exporting image:", error);
      alert("Failed to export image. Please ensure the source images are accessible.");
    }
  };


  const ControlSlider: React.FC<{
    label: string; value: number; onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    min: number; max: number; step?: number; unit?: string; onDoubleClick?: () => void;
  }> = ({ label, value, onChange, min, max, step = 1, unit = '', onDoubleClick }) => (
    <div className="mb-2">
      <div className="flex justify-between items-center text-xs mb-1">
        <label className="font-medium text-zinc-400 cursor-pointer" onDoubleClick={onDoubleClick} title="Double-click to reset">{label}</label>
        <span className="text-zinc-300 font-mono">{value.toFixed(label === 'Scale' ? 2 : 0)}{unit}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value} onChange={onChange}
        className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer range-slider"
      />
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-sm"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="w-full h-full flex"
      >
        {/* Main Viewer */}
        <div
          ref={containerRef}
          className="flex-1 flex items-center justify-center p-4 relative overflow-hidden"
          onWheel={handleWheel}
          onMouseDown={handlePanStart}
          onMouseMove={handlePanMove}
          onMouseUp={handlePanEnd}
          onMouseLeave={handlePanEnd}
          style={{ cursor: activeTool === 'pan' ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
        >
          <motion.div
            className="relative w-full h-full flex items-center justify-center"
            animate={{ x: viewState.x, y: viewState.y, scale: viewState.scale }}
            transition={{ type: 'tween', duration: 0.1, ease: 'linear' }}
          >
            {/* SMILE MODEL - RENDERED FIRST (BOTTOM LAYER) */}
            <motion.div
              drag={activeTool === 'transform'}
              onDragStart={() => setIsDraggingSmile(true)}
              onDragEnd={() => setIsDraggingSmile(false)}
              onDrag={(e, info) => {
                setTransform(t => ({
                  ...t,
                  x: t.x + info.delta.x / viewState.scale,
                  y: t.y + info.delta.y / viewState.scale,
                }));
              }}
              dragMomentum={false}
              className={`absolute ${activeTool === 'transform' ? (isDraggingSmile ? 'cursor-grabbing' : 'cursor-grab') : 'cursor-default'}`}
              style={{ x: transform.x, y: transform.y }}
            >
              <motion.img
                src={smileModelUrl}
                alt="Smile Model"
                className="pointer-events-none"
                animate={{
                  rotate: transform.rotate,
                  scaleX: transform.scale * (transform.width / 100),
                  scaleY: transform.scale * (transform.height / 100),
                }}
                style={{
                  filter: `brightness(${filters.brightness + filters.exposure}%) contrast(${filters.contrast}%) saturate(${filters.saturate}%) hue-rotate(${filters.hue}deg)`
                }}
                draggable="false"
              />
            </motion.div>

            {/* FACE IMAGE - RENDERED SECOND (TOP LAYER) */}
            <img
              ref={faceImageRef}
              src={faceImageUrl}
              alt="Patient Face"
              className="absolute max-w-full max-h-full object-contain pointer-events-none"
              style={{ opacity: faceOpacity }}
              onLoad={resetView}
              draggable="false"
            />
          </motion.div>
          {/* View Controls */}
          <div className="absolute bottom-4 left-4 z-20 flex items-center gap-1 p-1.5 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl">
            <button onClick={() => handleZoom(1.25)} title="Zoom In" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors"><ZoomInIcon className="w-5 h-5" /></button>
            <button onClick={() => handleZoom(0.8)} title="Zoom Out" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors"><ZoomOutIcon className="w-5 h-5" /></button>
            <button onClick={resetView} title="Reset View" className="p-2.5 rounded-full text-zinc-300 hover:bg-white/10 transition-colors"><RefreshCwIcon className="w-5 h-5" /></button>
          </div>
          {/* Tool Selector */}
          <div className="absolute top-4 left-4 z-20 flex items-center gap-1 p-1.5 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/10 shadow-2xl">
            <button onClick={() => setActiveTool('transform')} title="Transform" className={`relative p-2.5 rounded-full transition-colors ${activeTool === 'transform' ? 'text-teal-400' : 'text-zinc-300 hover:bg-white/10'}`}>
              {activeTool === 'transform' && <motion.div layoutId="tool-active-bg" className="absolute inset-0 bg-white/10 rounded-full" />}
              <TransformIcon className="w-5 h-5 relative z-10" />
            </button>
            <button onClick={() => setActiveTool('pan')} title="Pan" className={`relative p-2.5 rounded-full transition-colors ${activeTool === 'pan' ? 'text-teal-400' : 'text-zinc-300 hover:bg-white/10'}`}>
              {activeTool === 'pan' && <motion.div layoutId="tool-active-bg" className="absolute inset-0 bg-white/10 rounded-full" />}
              <PanToolIcon className="w-5 h-5 relative z-10" />
            </button>
          </div>
        </div>

        {/* Controls Sidebar */}
        <div className="w-80 bg-zinc-950/80 backdrop-blur-lg border-l border-white/10 flex flex-col">
          <header className="p-4 flex justify-between items-center border-b border-white/10 flex-shrink-0">
            <h2 className="text-lg font-bold text-zinc-100">Virtual Try-On Studio</h2>
            <button onClick={onClose} className="p-2 text-zinc-400 rounded-full hover:bg-white/10 transition-colors"><XIcon className="w-5 h-5" /></button>
          </header>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-sm font-semibold text-teal-400 mb-3">Face Image</h3>
              <ControlSlider label="Face Visibility" value={faceOpacity * 100} onChange={e => setFaceOpacity(Number(e.target.value) / 100)} min={0} max={100} unit="%" onDoubleClick={() => setFaceOpacity(1)} />
            </div>

            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-sm font-semibold text-teal-400 mb-3">Position & Size</h3>
              <ControlSlider label="Scale" value={transform.scale} onChange={e => setTransform(t => ({ ...t, scale: Number(e.target.value) }))} min={0.2} max={3} step={0.01} onDoubleClick={() => setTransform(t => ({ ...t, scale: 1 }))} />
              <ControlSlider label="Width" value={transform.width} onChange={e => setTransform(t => ({ ...t, width: Number(e.target.value) }))} min={50} max={150} unit="%" onDoubleClick={() => setTransform(t => ({ ...t, width: 100 }))} />
              <ControlSlider label="Height" value={transform.height} onChange={e => setTransform(t => ({ ...t, height: Number(e.target.value) }))} min={50} max={150} unit="%" onDoubleClick={() => setTransform(t => ({ ...t, height: 100 }))} />
              <ControlSlider label="Rotation" value={transform.rotate} onChange={e => setTransform(t => ({ ...t, rotate: Number(e.target.value) }))} min={-45} max={45} unit="°" onDoubleClick={() => setTransform(t => ({ ...t, rotate: 0 }))} />
              <p className="text-center text-xs text-zinc-500 mt-2">Tip: Double-click any label to reset</p>
            </div>

            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <h3 className="text-sm font-semibold text-teal-400 mb-3">Color Adjustments</h3>
              <ControlSlider label="Exposure" value={filters.exposure} onChange={e => setFilters(f => ({ ...f, exposure: Number(e.target.value) }))} min={-50} max={50} onDoubleClick={() => setFilters(f => ({ ...f, exposure: 0 }))} />
              <ControlSlider label="Brightness" value={filters.brightness} onChange={e => setFilters(f => ({ ...f, brightness: Number(e.target.value) }))} min={50} max={150} unit="%" onDoubleClick={() => setFilters(f => ({ ...f, brightness: 100 }))} />
              <ControlSlider label="Contrast" value={filters.contrast} onChange={e => setFilters(f => ({ ...f, contrast: Number(e.target.value) }))} min={50} max={150} unit="%" onDoubleClick={() => setFilters(f => ({ ...f, contrast: 100 }))} />
              <ControlSlider label="Saturation" value={filters.saturate} onChange={e => setFilters(f => ({ ...f, saturate: Number(e.target.value) }))} min={0} max={200} unit="%" onDoubleClick={() => setFilters(f => ({ ...f, saturate: 100 }))} />
              <ControlSlider label="Temperature" value={filters.temperature} onChange={e => setFilters(f => ({ ...f, temperature: Number(e.target.value) }))} min={4000} max={9000} step={50} unit="K" onDoubleClick={() => setFilters(f => ({ ...f, temperature: 6500 }))} />
              <ControlSlider label="Hue" value={filters.hue} onChange={e => setFilters(f => ({ ...f, hue: Number(e.target.value) }))} min={-30} max={30} unit="°" onDoubleClick={() => setFilters(f => ({ ...f, hue: 0 }))} />
            </div>
          </div>

          <footer className="p-4 border-t border-white/10 flex-shrink-0 space-y-3">
            <motion.button
              onClick={handleExport} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold bg-gradient-to-br from-teal-400 to-teal-600 text-white transition-all"
            >
              <DownloadIcon className="w-4 h-4" />
              Save Final Image
            </motion.button>
            <motion.button
              onClick={handleReset} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-white/10 text-zinc-300 hover:bg-white/20 transition-all"
            >
              <RefreshCwIcon className="w-3 h-3" />
              Reset to Default
            </motion.button>
          </footer>
        </div>
      </motion.div>
      <style>{`
        .range-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 2px;
          outline: none;
          transition: all 0.2s;
        }
        .range-slider:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .range-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          background: #2dd4bf;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #111827; /* dark bg color */
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .range-slider:active::-webkit-slider-thumb,
        .range-slider::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 8px #2dd4bf;
        }
        .range-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #2dd4bf;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #111827;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .range-slider:active::-moz-range-thumb,
        .range-slider::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 0 8px #2dd4bf;
        }
      `}</style>
    </motion.div>
  );
};

export default SmileStudio;